import { useEffect, useMemo, useRef, useState } from 'react'
import {
  api,
  ApiError,
  can,
  commitAccepted,
  refusalReason,
  type CommitResult,
  type ImportAnalysis,
  type ImportColumnChoices,
  type ImportCounts,
  type ImportDuplicates,
  type ImportFindReplace,
  type ImportPreview,
  type ImportPreviewFilter,
  type ImportPreviewRow,
  type ImportReport,
  type ImportServerDraft,
  type ImportValueSet,
} from '../lib/api'
import { errorWords, importingWords, plural } from '../lib/importWords'
import { useToast } from '../components/ui'
import { dropImportDraft, importAddress, loadImportDraft, readImportAddress, saveImportDraft } from '../lib/importDraft'
import { CommitProgress, CommitStopped, ConfirmPanel, followCommit, ResultPanel } from './import/CommitStep'
import { ColumnStep } from './import/ColumnsStep'
import { defaultMappingName, mappingName, personsChoices } from './import/columnTiers'
import {
  draftBody,
  draftFailedWords,
  DraftSync,
  DRAFT_QUIET_MS,
  DRAFT_RELOADED,
  DRAFT_SAVED,
  restoreDraft,
  type DraftEvent,
  type DraftValues,
  type Remember,
} from './import/draftSync'
import { CheckStep } from './import/ReviewStep'
import { changedFixedColumns, firstFilter, fixNoteWords, REVIEW_PAGE } from './import/reviewGrid'
import {
  initialAnswers,
  initialChoices,
  prefillValues,
  reachableStep,
  valuesBody,
  type ImportDraft,
  type ValueAnswers,
} from './import/shared'
import { Stepper } from './import/Stepper'
import { FileStep } from './import/UploadStep'
import { ValueStep } from './import/ValuesStep'
import '../styles/sweep.css'
import '../styles/import.css'

// Everything the tests and the other views read from this file is still here.
// The steps moved to views/import/, one file per step (release 3.3, I0, with no
// change in what the page does or says); this file keeps the wizard itself:
// what the address asks for, every answer, and the calls to the API.
export { Stepper } from './import/Stepper'
export { FileStep } from './import/UploadStep'
export { ColumnStep, pickerOptions } from './import/ColumnsStep'
export { ValueStep, openValues } from './import/ValuesStep'
export { CheckStep, fixKey } from './import/ReviewStep'
export {
  CommitProgress,
  CommitStopped,
  ConfirmPanel,
  followCommit,
  FOLLOW_MS,
  resultFromReport,
  resultHeadline,
  ResultPanel,
  type CommitFollow,
} from './import/CommitStep'
export {
  addLabel,
  initialAnswers,
  initialChoices,
  openQuestions,
  prefillValues,
  reachableStep,
  type ValueAnswers,
} from './import/shared'

// Bring vendors in (release 3.2): any vendor file, five steps.
//
//  1. Choose the file. The API reads it, finds the header row and the sheet,
//     and says what it found.
//  2. Match the columns. Each column of the file gets one of our fields, or is
//     kept as extra detail on each vendor, or left out.
//  3. Match the values. Each word the file uses for a fixed list of ours
//     (legal form, type of vendor, data they handle) is told once.
//  4. Check and fix. The rows as they will land, with what is wrong and the
//     fix in place; vendors with problems are held, the good ones go in.
//  5. Bring them in. One button, then the result with the count report, Undo
//     for 14 days, and contracts and MSAs. A large file is written in the
//     background (the commit answers 202): the page follows it on the count
//     report every 3 seconds, and a commit that stopped part way (a restart of
//     the server) can be carried on or undone from here.
//
// An import never sends anything: no email, no invitation, no questionnaire.
// The old "send the questionnaire to all of them now" offer on the result is
// gone on purpose (the CPO's rule); questionnaires go out from where they are
// planned, never from an import.
//
// Nothing internal reaches the screen: the API sends a plain label for every
// field, option, layout and outcome, and the keys and codes it recognises
// answers by are kept in state and sent back, never drawn (not even as the
// value of a drop-down option, which is why every select here is indexed).
// Going back never loses work: the column choices, the value answers and the
// import are kept, and only what a change makes stale is worked out again.
//
// Release 3.3 (I2): the server keeps a draft of the import (plan 4.8). Every
// Next, and every change after a second of quiet, writes it (draftSync.ts),
// and the step's footer says "Draft saved". An import opened by its address
// in a tab that holds nothing for it (a new tab, Import history, another day)
// reads the kept analysis and the draft and shows the step as it was. The tab
// still keeps its own copy (lib/importDraft.ts), so Back and a refresh need
// no round trip.

const PREVIEW_FILTERS: ImportPreviewFilter[] = ['all', 'attention', 'existing', 'held', 'add']

const IMPORT_FINISHED = 'This import can no longer be changed. Open it from Import history.'

// The column choices of the matching last applied, read back from its
// signature (JSON of [importId, body]); null when none was applied yet.
export function appliedColumns(mappedFor: string): ImportColumnChoices | null {
  if (!mappedFor) return null
  try {
    const parsed = JSON.parse(mappedFor) as [string, { columns?: ImportColumnChoices }]
    return parsed[1]?.columns ?? null
  } catch {
    return null
  }
}

// The offset of the last page of rows, 50 a page.
export function lastPageOffset(total: number): number {
  return total <= 0 ? 0 : Math.floor((total - 1) / REVIEW_PAGE) * REVIEW_PAGE
}

export function Import() {
  const toast = useToast()
  // What the address asks for, and what this tab kept for it.
  const [start] = useState(() => {
    const at = readImportAddress(typeof window === 'undefined' ? '' : window.location.hash)
    const kept = at.id ? loadImportDraft<ImportDraft>(at.id) : null
    return { at, kept }
  })
  const kept = start.kept
  const [lost, setLost] = useState(false)
  // Why an address could not be opened, in the server's words.
  const [lostWhy, setLostWhy] = useState('')
  // An address this tab holds nothing for: the import is read from the server.
  const [resuming, setResuming] = useState(start.at.id !== '' && !kept)
  const [step, setStep] = useState(() => (kept?.committed && start.at.result ? 5 : reachableStep(start.at.step, kept)))
  const [file, setFile] = useState<File | null>(null)
  const [analysis, setAnalysis] = useState<ImportAnalysis | null>(kept?.analysis ?? null)
  const [choices, setChoices] = useState<ImportColumnChoices>(kept?.choices ?? {})
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>(kept?.confirmed ?? {})
  // The columns a person chose by hand: "Matched (you)", and a later
  // suggestion (a file read again) never overwrites them.
  const [byPerson, setByPerson] = useState<string[]>(kept?.byPerson ?? [])
  const byPersonSet = useMemo(() => new Set(byPerson), [byPerson])
  const [remember, setRemember] = useState<Remember>(
    () => kept?.remember ?? { on: !kept?.analysis?.savedMapping, name: kept?.analysis ? defaultMappingName(kept.analysis) : '' },
  )
  const [fill, setFill] = useState<Record<string, string>>(kept?.fill ?? {})
  // The server draft: its version, what was last written to it, the footer's
  // line, and whose it is when it is not this person's to change.
  const [draftVersion, setDraftVersion] = useState<number | undefined>(kept?.draftVersion)
  const [sentSig, setSentSig] = useState(kept?.sentSig ?? '')
  const [draftNote, setDraftNote] = useState('')
  const [readOnly, setReadOnly] = useState('')
  const [syncFor, setSyncFor] = useState('')
  const drafts = useRef<DraftSync | null>(null)
  const sentStep = useRef(-1)
  // The matching last saved as a mapping, and the one line if saving it failed.
  const [savedFor, setSavedFor] = useState(kept?.savedFor ?? '')
  const [rememberNote, setRememberNote] = useState('')
  // Upload's "Change": Match columns opens with every row shown.
  const [allColumns, setAllColumns] = useState(false)
  const [answers, setAnswers] = useState<Record<string, string>>(kept?.answers ?? {})
  const [valueSets, setValueSets] = useState<ImportValueSet[] | null>(kept?.valueSets ?? null)
  const [valuesFor, setValuesFor] = useState(kept?.valuesFor ?? '')
  const [valueAnswers, setValueAnswers] = useState<ValueAnswers>(kept?.valueAnswers ?? {})
  const [skippedValues, setSkippedValues] = useState(kept?.skippedValues ?? false)
  const [duplicates, setDuplicates] = useState<ImportDuplicates>(kept?.duplicates ?? 'FILL_EMPTY')
  const [mappedFor, setMappedFor] = useState(kept?.mappedFor ?? '')
  const [counts, setCounts] = useState<ImportCounts | null>(kept?.counts ?? null)
  const [mapNote, setMapNote] = useState(kept?.mapNote ?? '')
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [filter, setFilter] = useState<ImportPreviewFilter>(kept?.filter ?? 'all')
  // Review and fix (I4): the page of rows shown (from 0), kept in the draft so
  // Back, a refresh and Continue return to it; the file columns that carry a
  // fix; and the one line when such a column is matched differently now.
  const [page, setPage] = useState(kept?.page ?? 0)
  const [fixedCols, setFixedCols] = useState<string[]>(kept?.fixedColumns ?? [])
  const [fixNote, setFixNote] = useState('')
  const [loading, setLoading] = useState(false)
  const previewSeq = useRef(0)
  const [committed, setCommitted] = useState<CommitResult | null>(kept?.committed ?? null)
  // A commit written in the background: following it, how far it got, and
  // the report of one that stopped part way.
  const [following, setFollowing] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [stopped, setStopped] = useState<ImportReport | null>(null)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState<unknown>(null)

  const id = analysis?.importId ?? ''

  // An address this tab holds nothing for: Upload, with one line saying why.
  useEffect(() => {
    if (lost) window.location.replace(importAddress('', 1))
  }, [lost])

  // Every answer is kept in the tab as it is given, so Back, Forward and a
  // refresh land on the same step with the same answers.
  useEffect(() => {
    if (!analysis) return
    saveImportDraft<ImportDraft>(analysis.importId, {
      analysis, choices, confirmed, answers, valueSets, valuesFor, valueAnswers, skippedValues,
      duplicates, mappedFor, counts, mapNote, filter, committed,
      byPerson, remember, fill, draftVersion, sentSig, savedFor, page, fixedColumns: fixedCols,
    })
  }, [analysis, choices, confirmed, answers, valueSets, valuesFor, valueAnswers, skippedValues, duplicates, mappedFor, counts, mapNote, filter, committed, byPerson, remember, fill, draftVersion, sentSig, savedFor, page, fixedCols])

  // Browser Back and Forward move between the steps. Back from the result of
  // a finished import does not reopen it: it goes to Import history (B9).
  const live = useRef({ analysis, valueSets, skippedValues, counts, committed, step, filter, page })
  live.current = { analysis, valueSets, skippedValues, counts, committed, step, filter, page }

  // The address go() just pushed: its own hashchange is not a Back, and the
  // step it set is already the one shown.
  const pushed = useRef('')
  useEffect(() => {
    const sync = () => {
      if (window.location.hash === pushed.current) {
        pushed.current = ''
        return
      }
      const at = readImportAddress(window.location.hash)
      const cur = live.current
      if (!window.location.hash.startsWith('#/import')) return
      if (cur.committed && !at.result) {
        window.location.replace('#/imports')
        return
      }
      if (!at.id) {
        if (!cur.analysis) setStep(1)
        return
      }
      if (at.id !== cur.analysis?.importId) return
      setError(null)
      setStep(at.result ? 5 : reachableStep(at.step, cur))
    }
    window.addEventListener('hashchange', sync)
    return () => window.removeEventListener('hashchange', sync)
  }, [])

  // ------------------------------------------------ the server draft

  // What the draft keeps of the wizard as it stands, the filter and the page
  // of Review and fix included (I4).
  const draftValues: DraftValues = {
    choices, confirmed, byPerson, answers, remember, valueAnswers, duplicates, fill, filter, page,
  }

  // Takes a draft read back from the server: after a write was refused as
  // stale (another tab saved since), or when this tab's copy is older.
  function adopt(d: ImportServerDraft) {
    const cur = live.current
    if (!cur.analysis) return
    const v = restoreDraft(cur.analysis, d)
    setChoices(v.choices)
    setConfirmed(v.confirmed)
    setByPerson(v.byPerson)
    setAnswers(v.answers)
    setRemember(v.remember)
    setValueAnswers(v.valueAnswers)
    setDuplicates(v.duplicates)
    setFill(v.fill)
    setDraftVersion(d.version)
    // What the server holds now is what was last written: nothing to send.
    setSentSig(JSON.stringify(draftBody(cur.step, { ...v, filter: cur.filter, page: cur.page }, cur.analysis)))
  }

  const onDraft = useRef<(e: DraftEvent) => void>(() => {})
  onDraft.current = (e: DraftEvent) => {
    if (e.kind === 'saved') {
      setDraftVersion(e.draft.version)
      setDraftNote(DRAFT_SAVED)
    } else if (e.kind === 'reloaded') {
      adopt(e.draft)
      setDraftNote(DRAFT_RELOADED)
    } else if (e.error instanceof ApiError && (e.error.status === 403 || e.error.status === 410)) {
      // Not this person's import, or discarded or set aside since: it is read
      // only from here on, and the server's words say why.
      setDraftNote('')
      setReadOnly(errorWords(e.error))
    } else {
      setDraftNote(draftFailedWords(e.error))
    }
  }

  function startSync(id: string, version: number) {
    drafts.current = new DraftSync(id, version, (e) => onDraft.current(e))
    setSyncFor(id)
  }

  // Opening an import by its address. A tab that holds nothing for it reads
  // the kept analysis and the draft, and shows the step as it was; a tab that
  // does learns the draft's version, and takes the server's copy if another
  // tab changed it since. A finished import is read from its result, not here.
  useEffect(() => {
    const at = start.at
    const k = start.kept
    if (!at.id || k?.committed) return
    let on = true
    Promise.all([k ? Promise.resolve(k.analysis) : api.importAnalysisKept(at.id), api.importDraft(at.id)])
      .then(([a, d]) => {
        if (!on) return
        if (!d.canEdit && d.yours && !k) {
          setLostWhy(IMPORT_FINISHED)
          setLost(true)
          return
        }
        if (!d.yours) setReadOnly('This import is ' + d.ownerName + "'s. You can read it; only they can change it.")
        else if (!d.canEdit) setReadOnly(IMPORT_FINISHED)
        startSync(at.id, d.version)
        if (!k) {
          const v = restoreDraft(a, d)
          const n = reachableStep(at.step, { analysis: a, valueSets: null, skippedValues: false, counts: null })
          setAnalysis(a)
          setChoices(v.choices)
          setConfirmed(v.confirmed)
          setByPerson(v.byPerson)
          setAnswers(v.answers)
          setRemember(v.remember)
          setValueAnswers(v.valueAnswers)
          setDuplicates(v.duplicates)
          setFill(v.fill)
          const f = PREVIEW_FILTERS.includes(v.filter as ImportPreviewFilter) ? (v.filter as ImportPreviewFilter) : 'all'
          setFilter(f)
          setPage(v.page)
          setDraftVersion(d.version)
          setSentSig(JSON.stringify(draftBody(n, { ...v, filter: f }, a)))
          sentStep.current = n
          setStep(n)
          if (window.location.hash !== importAddress(a.importId, n)) window.location.replace(importAddress(a.importId, n))
        } else if (k.draftVersion !== undefined && d.version > k.draftVersion) {
          adopt(d)
          setDraftNote(DRAFT_RELOADED)
        } else {
          setDraftVersion(d.version)
        }
      })
      .catch((err) => {
        if (!on) return
        if (!k) {
          setLostWhy(errorWords(err))
          setLost(true)
        } else {
          // The draft could not be read: this tab works from its own copy,
          // and the first write says whether the server takes it.
          startSync(at.id, k.draftVersion ?? 0)
        }
      })
      .finally(() => {
        if (on) setResuming(false)
      })
    return () => {
      on = false
    }
    // Once, for the address the page opened on.
  }, [])

  // Writes the draft: at once when the step changed (Next, Back), and after a
  // second of quiet for any other change. Nothing is written for an import
  // that is being imported or is finished, or that is not this person's.
  const writable = Boolean(analysis && syncFor === analysis.importId && !readOnly && !committed && !following && !stopped && busy !== 'commit')
  const wantBody = analysis && writable ? draftBody(step, draftValues, analysis) : null
  const wantSig = wantBody ? JSON.stringify(wantBody) : ''
  const pending = useRef<{ sig: string; body: ReturnType<typeof draftBody> | null }>({ sig: '', body: null })
  pending.current = { sig: wantSig, body: wantBody }
  useEffect(() => {
    const s = drafts.current
    if (!wantBody || !s || wantSig === sentSig) return
    const send = () => {
      sentStep.current = step
      setSentSig(wantSig)
      void s.save(wantBody)
    }
    if (step !== sentStep.current) {
      send()
      return
    }
    const t = window.setTimeout(send, DRAFT_QUIET_MS)
    return () => window.clearTimeout(t)
    // wantSig stands for every value the body is made of.
  }, [wantSig, sentSig, step])

  // Leaving the page inside the quiet second still writes the last change.
  const sentRef = useRef(sentSig)
  sentRef.current = sentSig
  useEffect(
    () => () => {
      const p = pending.current
      if (p.body && p.sig !== sentRef.current) void drafts.current?.save(p.body)
    },
    [],
  )

  // "Remember this mapping as": saved once the matching is applied (the
  // server saves the matching an import applied, with its value answers and
  // duplicate rule, plan 4.7), and again only when that matching or the name
  // changed. A failure is one line and never stops the import.
  async function rememberMapping(applied: string) {
    if (!id || !analysis || !remember.on || readOnly) return
    const name = mappingName(remember.name) || defaultMappingName(analysis)
    const want = applied + '\n' + name
    if (want === savedFor) return
    try {
      await api.saveImportMapping(id, name)
      setSavedFor(want)
      setRememberNote('')
    } catch (err) {
      setRememberNote('Not remembered as a mapping: ' + errorWords(err))
    }
  }

  // A refresh on Review and fix: the page of rows it was on is read again.
  useEffect(() => {
    if (step === 4 && counts && !preview && id && !loading) void loadPreview(filter, page * REVIEW_PAGE)
    // Only on arriving at the step with nothing to show.
  }, [step, id])

  function finish(r: CommitResult) {
    setCommitted(r)
    toast(plural(r.committed, 'vendor') + ' added to the register.')
    // The finished import replaces its own Import step in the history (B9).
    if (id) window.location.replace(importAddress(id, 'result'))
  }

  // Reads the count report every 3 seconds while a commit runs in the
  // background, and stops when the page goes away.
  useEffect(() => {
    if (!following || !id) return
    return followCommit(id, {
      progress: (p) => {
        setError(null)
        setProgress(p)
      },
      finished: (r) => {
        setFollowing(false)
        setProgress(null)
        finish(r)
      },
      stopped: (r) => {
        setFollowing(false)
        setProgress(null)
        setStopped(r)
      },
      failed: (err, ended) => {
        setError(err)
        if (ended) setFollowing(false)
      },
    })
    // finish only raises a toast and sets the result; following and the id
    // are what start and stop the reads.
  }, [following, id])

  // One history entry per step: the address is pushed, so Back comes here.
  function go(n: number) {
    setError(null)
    setStep(n)
    if (typeof window !== 'undefined') {
      const want = importAddress(id, n)
      if (window.location.hash !== want) {
        pushed.current = want
        window.location.hash = want
      }
      window.scrollTo?.({ top: 0 })
    }
  }

  function startOver() {
    if (id && committed) dropImportDraft(id)
    setLost(false)
    if (typeof window !== 'undefined' && window.location.hash !== '#/import') window.location.hash = '#/import'
    setStep(1)
    setFile(null)
    setAnalysis(null)
    setChoices({})
    setConfirmed({})
    setAnswers({})
    setValueSets(null)
    setValuesFor('')
    setValueAnswers({})
    setSkippedValues(false)
    setDuplicates('FILL_EMPTY')
    setMappedFor('')
    setCounts(null)
    setMapNote('')
    setPreview(null)
    setFilter('all')
    setPage(0)
    setFixedCols([])
    setFixNote('')
    setCommitted(null)
    setFollowing(false)
    setProgress(null)
    setStopped(null)
    setError(null)
    setByPerson([])
    setRemember({ on: true, name: '' })
    setFill({})
    setSavedFor('')
    setRememberNote('')
    setDraftNote('')
    setReadOnly('')
    setLostWhy('')
    setDraftVersion(undefined)
    setSentSig('')
    drafts.current = null
    setSyncFor('')
  }

  // Reading the file again with another sheet or header row makes a new
  // import. The columns a person chose by hand are kept for columns of the
  // same name; the rest take the new reading's suggestions.
  async function read(f: File, opts: { sheet?: string; headerRow?: number } = {}) {
    setBusy('read')
    setError(null)
    try {
      const a = await api.analyseImport(f, opts)
      const same = file === f
      const mine = same ? personsChoices(choices, byPerson) : {}
      const v: DraftValues = {
        choices: initialChoices(a, mine),
        confirmed: {},
        byPerson: Object.keys(mine).filter((c) => a.columns.some((x) => x.column === c)),
        answers: initialAnswers(a, same ? answers : {}),
        remember: same && remember.name ? remember : { on: !a.savedMapping, name: defaultMappingName(a) },
        valueAnswers: same ? valueAnswers : {},
        duplicates: same ? duplicates : 'FILL_EMPTY',
        fill: {},
        filter: 'all',
        page: 0,
      }
      setFile(f)
      setAnalysis(a)
      setChoices(v.choices)
      setAnswers(v.answers)
      setConfirmed(v.confirmed)
      setByPerson(v.byPerson)
      setRemember(v.remember)
      setValueAnswers(v.valueAnswers)
      setDuplicates(v.duplicates)
      setFill(v.fill)
      setSavedFor('')
      setRememberNote('')
      setDraftNote('')
      setReadOnly('')
      // A new import has no draft yet (version 0), and the server answers one
      // with the importer's suggestions, so a fresh file writes nothing until
      // a change; a file read again with choices kept writes them.
      startSync(a.importId, 0)
      setDraftVersion(0)
      setSentSig(v.byPerson.length > 0 ? '' : JSON.stringify(draftBody(1, v, a)))
      sentStep.current = 1
      setFilter('all')
      setValueSets(null)
      setValuesFor('')
      setSkippedValues(false)
      setMappedFor('')
      setCounts(null)
      setMapNote('')
      setPreview(null)
      setStep(1)
      setLost(false)
      // The file read is Upload's own entry: the address now names the import.
      window.location.replace(importAddress(a.importId, 1))
    } catch (err) {
      setError(err)
    } finally {
      setBusy('')
    }
  }

  async function template(format: 'xlsx' | 'csv') {
    setError(null)
    try {
      const { blob, fileName } = await api.importTemplate(format)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err)
    }
  }

  // Reads one page of rows: 50, never the whole file (plan 4.5). A page past
  // the end (the rows on it were fixed and left the filter) reads the last one.
  async function loadPreview(f: ImportPreviewFilter, offset: number) {
    if (!id) return
    // Two quick reads (a filter, then another) can answer out of order: only
    // the latest one asked is shown.
    const seq = ++previewSeq.current
    setLoading(true)
    try {
      let p = await api.importPreview(id, { filter: f, offset, limit: REVIEW_PAGE })
      if (seq !== previewSeq.current) return
      if (p.rows.length === 0 && offset > 0 && p.total > 0) {
        p = await api.importPreview(id, { filter: f, offset: lastPageOffset(p.total), limit: REVIEW_PAGE })
        if (seq !== previewSeq.current) return
      }
      setPreview(p)
      setCounts(p.counts)
      setPage(Math.floor((p.offset ?? 0) / REVIEW_PAGE))
      noteFixed(p.rows.flatMap((r) => Object.keys(r.fixes ?? {})))
    } catch (err) {
      if (seq === previewSeq.current) setError(err)
    } finally {
      if (seq === previewSeq.current) setLoading(false)
    }
  }

  // The file columns that carry a fix ("@" keys are choices for a field, not
  // a column of the file, and stay whatever the matching).
  function noteFixed(columns: string[]) {
    const add = columns.filter((c) => c !== '' && !c.startsWith('@'))
    if (add.length === 0) return
    setFixedCols((cur) => (add.every((c) => cur.includes(c)) ? cur : [...new Set([...cur, ...add])].sort()))
  }

  // Applies the matching when anything in it changed since it was last
  // applied, then shows the first page: the rows to fix when there are any
  // (problems first, plan 4.5), else everything. A change of the duplicate
  // rule keeps the filter the person chose. When columns that carry fixes
  // are matched differently now, one line says so (the server keeps every
  // fix beside the file and checks it again under the new matching).
  async function applyMapping(sets: ImportValueSet[], vals: ValueAnswers, dupes: ImportDuplicates, keepFilter = false): Promise<boolean> {
    if (!id) return false
    const body = { columns: choices, values: valuesBody(sets, vals), answers, duplicates: dupes }
    const sig = JSON.stringify([id, body])
    if (sig === mappedFor && counts) {
      void rememberMapping(sig)
      return true
    }
    setBusy('mapping')
    setError(null)
    try {
      const r = await api.applyImportMapping(id, body)
      const before = appliedColumns(mappedFor)
      setFixNote(before ? fixNoteWords(changedFixedColumns(before, choices, fixedCols), choices) : '')
      setCounts(r.counts)
      setMapNote(r.note ?? '')
      setMappedFor(sig)
      const f = keepFilter ? filter : firstFilter(r.counts)
      setFilter(f)
      void rememberMapping(sig)
      await loadPreview(f, 0)
      return true
    } catch (err) {
      setError(err)
      return false
    } finally {
      setBusy('')
    }
  }

  async function toValues() {
    if (!analysis) return
    const sig = JSON.stringify([analysis.importId, choices, answers])
    let sets = valueSets
    let vals = valueAnswers
    if (sig !== valuesFor || !sets) {
      setBusy('values')
      setError(null)
      try {
        const r = await api.importValues(analysis.importId, { columns: choices, answers })
        sets = (r.valueSets ?? []).filter((s) => (s.values?.length ?? 0) > 0)
        vals = prefillValues(sets, valueAnswers)
        setValueSets(sets)
        setValueAnswers(vals)
        setValuesFor(sig)
      } catch (err) {
        setError(err)
        setBusy('')
        return
      }
      setBusy('')
    }
    if (sets.length === 0) {
      setSkippedValues(true)
      if (await applyMapping(sets, vals, duplicates)) go(4)
    } else {
      setSkippedValues(false)
      go(3)
    }
  }

  async function toCheck() {
    if (await applyMapping(valueSets ?? [], valueAnswers, duplicates)) go(4)
  }

  async function changeDuplicates(d: ImportDuplicates) {
    setDuplicates(d)
    await applyMapping(valueSets ?? [], valueAnswers, d, true)
  }

  async function fixRow(row: ImportPreviewRow, key: string, value: string) {
    // Only the cell changed: the server merges it into the row's fixes, so a
    // second quick edit on the row never sends back an older value of the first.
    const r = await api.fixImportRow(id, row.id, { fixes: { [key]: value } })
    setPreview((p) => (p ? { ...p, counts: r.counts, rows: p.rows.map((x) => (x.id === row.id ? r.row : x)) } : p))
    setCounts(r.counts)
    noteFixed([key])
  }

  async function holdRow(row: ImportPreviewRow, hold: boolean) {
    const r = await api.fixImportRow(id, row.id, { hold })
    setPreview((p) => (p ? { ...p, counts: r.counts, rows: p.rows.map((x) => (x.id === row.id ? r.row : x)) } : p))
    setCounts(r.counts)
  }

  async function fixAll(column: string, from: string, to: string) {
    const r = await api.fixImportColumn(id, { column, from, to })
    setCounts(r.counts)
    noteFixed([column])
    toast('Changed ' + from + ' to ' + to + ' in ' + plural(r.changed, 'vendor') + '.')
    await loadPreview(filter, preview?.offset ?? 0)
  }

  // Find and replace in one column (I1's call): a preview writes nothing; a
  // replace is a fix beside the file on every row it changed, and the page is
  // read again so the grid shows it.
  async function findReplace(req: ImportFindReplace) {
    const r = await api.findReplaceImport(id, req)
    if (!req.preview) {
      if (r.counts) setCounts(r.counts)
      if (r.changed > 0) noteFixed([req.column])
      toast(r.note)
      await loadPreview(filter, preview?.offset ?? 0)
    }
    return r
  }

  // Brings the rows in, or carries on a commit that stopped part way (the same
  // call). A small batch answers with the result; a large one answers 202 and
  // is followed on the count report until the result is there.
  async function commit() {
    setBusy('commit')
    setError(null)
    try {
      const r = await api.commitImport(id)
      setStopped(null)
      if (commitAccepted(r)) {
        const total = (r.done ?? 0) + (r.remaining ?? 0)
        setProgress({ done: r.done ?? 0, total })
        setFollowing(true)
        toast(importingWords(total))
      } else {
        finish(r)
      }
    } catch (err) {
      setError(err)
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="p1-page imw-page">
      <div className="p1-head">
        <div>
          <h1>Import vendors</h1>
        </div>
        <div className="p1-actions">
          <a className="button ghost" href="#/imports">
            Import history
          </a>
        </div>
      </div>
      <Stepper step={step} />

      {lost && step === 1 && (
        <p className="note warn" role="status">
          {lostWhy || 'That import is not open in this tab any more. Upload the file again, or open it from Import history.'}
        </p>
      )}
      {readOnly && !resuming && (
        <p className="note" role="status">
          {readOnly}
        </p>
      )}
      {draftNote && step >= 3 && step !== 4 && (
        <p className={'imw-small imw-draft-note' + (draftNote === DRAFT_SAVED ? '' : ' imw-draft-said')} role={draftNote === DRAFT_SAVED ? undefined : 'status'}>
          {draftNote}
        </p>
      )}
      {rememberNote && step >= 2 && (
        <p className="imw-small imw-remember-note" role="status">
          {rememberNote}
        </p>
      )}

      {resuming && (
        <p className="imw-lead" role="status">
          Opening the import...
        </p>
      )}

      {step === 1 && !resuming && (
        <FileStep
          analysis={analysis}
          busy={busy === 'read'}
          error={error}
          onFile={(f) => void read(f)}
          onTemplate={(f) => void template(f)}
          onReread={(opts) => {
            if (file) void read(file, opts)
          }}
          fileHeld={file !== null || !analysis}
          onNext={() => {
            setAllColumns(false)
            go(2)
          }}
          onChangeMapping={() => {
            setAllColumns(true)
            go(2)
          }}
          onAnother={startOver}
        />
      )}

      {step === 2 && analysis && (
        <ColumnStep
          analysis={analysis}
          choices={choices}
          confirmed={confirmed}
          byPerson={byPersonSet}
          answers={answers}
          remember={remember}
          busy={busy !== ''}
          error={error}
          draftNote={draftNote}
          showAllFirst={allColumns}
          readOnly={Boolean(readOnly)}
          onChoose={(column, value) => {
            setChoices((cur) => ({ ...cur, [column]: value }))
            setByPerson((cur) => (cur.includes(column) ? cur : [...cur, column]))
          }}
          onConfirm={(columns) =>
            setConfirmed((cur) => {
              const next = { ...cur }
              for (const c of columns) next[c] = true
              return next
            })
          }
          onAnswer={(key, code) => setAnswers((cur) => ({ ...cur, [key]: code }))}
          onRemember={setRemember}
          onReset={() => {
            setChoices(initialChoices(analysis))
            setAnswers(initialAnswers(analysis))
            setConfirmed({})
            setByPerson([])
          }}
          onBack={() => go(1)}
          onNext={() => void toValues()}
        />
      )}

      {step === 3 && valueSets && (
        <ValueStep
          sets={valueSets}
          answers={valueAnswers}
          busy={busy !== ''}
          error={error}
          onAnswer={(field, value, code) =>
            setValueAnswers((cur) => ({ ...cur, [field]: { ...(cur[field] ?? {}), [value]: code } }))
          }
          onBack={() => go(2)}
          onNext={() => void toCheck()}
        />
      )}

      {step === 4 && counts && analysis && (
        <CheckStep
          analysis={analysis}
          choices={choices}
          counts={counts}
          preview={preview}
          filter={filter}
          duplicates={duplicates}
          loading={loading}
          busy={busy !== ''}
          skippedValues={skippedValues}
          error={error}
          readOnly={Boolean(readOnly)}
          draftNote={draftNote}
          fixNote={fixNote}
          onFilter={(f) => {
            setFilter(f)
            void loadPreview(f, 0)
          }}
          onPage={(o) => void loadPreview(filter, o)}
          onDuplicates={(d) => void changeDuplicates(d)}
          onFix={fixRow}
          onHold={holdRow}
          onFixAll={fixAll}
          onFindReplace={findReplace}
          onBack={() => go(skippedValues ? 2 : 3)}
          onNext={() => go(5)}
        />
      )}

      {step === 5 && counts && !committed && !following && !stopped && (
        <ConfirmPanel
          counts={counts}
          busy={busy === 'commit'}
          error={error}
          allowed={can('vendor.create')}
          refusal={refusalReason('vendor.create')}
          onBack={() => go(4)}
          onCommit={() => void commit()}
        />
      )}

      {step === 5 && !committed && following && <CommitProgress progress={progress} error={error} />}

      {step === 5 && !committed && !following && stopped && (
        <CommitStopped
          report={stopped}
          importId={id}
          mayCreate={can('vendor.create')}
          busy={busy === 'commit'}
          error={error}
          onCarryOn={() => void commit()}
          onAnother={startOver}
        />
      )}

      {step === 5 && committed && (
        <ResultPanel result={committed} importId={committed.importId || id} mayCreate={can('vendor.create')} onAnother={startOver} />
      )}
    </div>
  )
}
